<!-- eslint-disable vue/no-restricted-html-elements -->
<template>
  <splide
    id="productGalleryCarousel"
    ref="splide"
    class="product-gallery-carousel"
    :class="[
      $blockClass(blockClass, 'carousel'),
      `product-gallery-carousel--${namespace}`,
    ]"
    :has-track="false"
    :options="localConfig"
    :aria-label="$string('product.gallery.label', { replace: { product: product.title } })"
    @splide:mounted="handleSplideMounted"
    @splide:moved="handleSplideMoved"
  >
    <splide-track>
      <splide-slide
        v-for="(mediaItem, index) of media"
        :key="`media-${product.handle}-${index}`"
      >
        <div
          v-if="zoom && (mediaItem.mediaType === 'image' || mediaItem.mediaType === 'model')"
          ref="pinchZoom"
          class="product-gallery-carousel__zoom"
          :class="[
            $blockClass(blockClass, 'zoom'),
            { 'product-gallery-carousel__model-viewer': mediaItem.mediaType === 'model' }
          ]"
        >
          <div
            v-if="mediaItem.mediaType === 'image'"
            class="product-gallery-carousel__zoom-image-container"
          >
            <img
              class="product-gallery-carousel__zoom-image"
              :alt="mediaItem.alt"
              :loading="index > 0 ? 'lazy' : null"
              :src="mediaItem.image.src"
            >
          </div>
        </div>

        <responsive-image
          v-else-if="mediaItem.mediaType === 'model' && isMobile && !isModal"
          :block-class="blockClass"
          :image="mediaItem.previewImage"
          :fetchpriority-high="index > 1 ? false : true"
          :lazyload="index > 0"
          min-max="300-1000"
          ratio="3:4"
          :sizes="sizes || '100vw'"
          tabindex="-1"
          @click.prevent="handleZoomClick(mediaItem.position)"
        />

        <div
          v-else-if="mediaItem.mediaType === 'video'"
          class="product-gallery-carousel__video-container"
        >
          <video-player-mini
            class="product-gallery-carousel__video"
            :image="mediaItem.previewImage"
            :is-active="getVideoActive(mediaItem.position)"
            :sizes="sizes"
            :video="getVideoSource(mediaItem.sources)"
          />
        </div>

        <!--
          Not ideal to use a div as a button but there is already an
          accessible button in product gallery for keyboard navigation.
        -->
        <responsive-image
          v-else
          :block-class="blockClass"
          :image="mediaItem.image"
          :fetchpriority-high="index > 1 ? false : true"
          :lazyload="isMobile && index > 0 || !isMobile && index > 1"
          min-max="300-1000"
          ratio="3:4"
          :sizes="sizes || '100vw'"
          tabindex="-1"
          @click.prevent="handleZoomClick(mediaItem.position)"
        />
      </splide-slide>
    </splide-track>

    <icon-loading
      v-if="zoom"
      class="product-gallery-carousel__loading"
    />

    <div
      v-if="config.arrows"
      class="splide__arrows"
    >
      <btn
        class="splide__arrow splide__arrow--prev"
        :label="$string('accessibility.carousel.previous')"
        modifiers="round"
        type="button"
      >
        <template #icon>
          <icon-arrow-left />
        </template>
      </btn>

      <btn
        class="splide__arrow splide__arrow--next"
        :label="$string('accessibility.carousel.next')"
        modifiers="round"
        type="button"
      >
        <template #icon>
          <icon-arrow-right />
        </template>
      </btn>
    </div>

    <ul
      v-if="config.pagination"
      class="product-gallery-carousel__pagination splide__pagination"
      :class="$blockClass(blockClass, 'pagination')"
    />

    <!-- Brand tile has to be outside track to avoid carousel issues -->
    <div
      v-if="renderBrandTiles"
      class="product-gallery-carousel__brand-tile"
    >
      <brand-tile
        :background-color="brandTileBackgroundColour"
        :brand-tiles="brandTiles"
      />
    </div>
  </splide>
</template>

<script>

/**
 * Vue: Product gallery carousel (product-gallery-carousel)
 * -----------------------------------------------------------------------------
 * Splide powered carousel shared between gallery and modal.
 *
 * @param {String} [blockClass] - Block class to add to elements.
 * @param {String} brandTileBackgroundColour - Background colour for brand tile.
 * @param {Array} brandTiles - Brand tiles content.
 * @param {Boolean} [buttons] - Enable buttons to open gallery modal.
 * @param {Object} config - Splide carousel config.
 * @param {Number} currentMediaPosition - Current media position.
 * @param {String} currentNamespace - Current gallery.
 * @param {String} namespace - Namespace, either 'gallery' or 'modal'.
 * @param {Function} pinchZoomApi - Pinch zoom API, imported into product
 * gallery modal to save file size of main product.
 * @param {Object} product - Liquid local product object.
 * @param {String} sizes - Sizes attribute.
 * @param {Boolean} [videos] - Enable video display.
 * @param {Boolean} [zoom] - Enable pinch zoom functionality.
 * @emits updateCurrentMediaPosition - Emit carousel media position.
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'

import BrandTile from '~async/product-gallery-carousel/brand-tile'
import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import VideoPlayerMini from '~async/video-player-mini/video-player-mini'

import IconArrowLeft from '~icons/direction/arrow/left.svg'
import IconArrowRight from '~icons/direction/arrow/right.svg'
import IconLoading from '~icons/misc/loading.svg'

import { values as breakpoints } from '~/config/breakpoints'
import { computeMediaIndex } from '~/helpers/vue'

export default {
  name: 'ProductGalleryCarousel',

  components: {
    BrandTile,
    Btn,
    IconArrowLeft,
    IconArrowRight,
    IconLoading,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    VideoPlayerMini,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    brandTileBackgroundColour: {
      type: String,
      default: '#f4f4f4',
    },
    brandTiles: {
      type: Array,
      default: () => [],
    },
    buttons: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    currentMediaPosition: {
      type: Number,
      default: 1,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
    },
    namespace: {
      type: String,
      default: 'gallery',
    },
    pinchZoomApi: {
      type: Function,
      default: () => ({}),
    },
    product: {
      type: [Boolean, Object],
      default: () => ({
        handle: '',
        images: [],
        media: [],
        title: '',
      }),
    },
    sizes: {
      type: String,
      default: '',
    },
    videos: {
      type: Boolean,
      default: false,
    },
    zoom: {
      type: Boolean,
      default: false,
    },
    isModal: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-current-media-position': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      brandTileActive: false,
      localConfig: {},
      localSlideIndex: 0,
      pinchZoom: [],
      pinchZoomActive: false,
      startedPinchZoom: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute current media index based on current media position within the
     * filtered media array.
     * @returns {Number}
     */
    currentMediaIndex() {
      return computeMediaIndex(this.media, this.currentMediaPosition)
    },

    /**
     * Compute media array based on if videos are included.
     * @returns {Array}
     */
    media() {
      if (!this.product.media) {
        return []
      }

      return this.videos
        ? this.product.media
        : this.product.media.filter((media) => media.mediaType === 'image' || media.mediaType === 'model')
    },

    /**
     * Compute if current gallery namespace is active.
     * @returns {Boolean}
     */
    namespaceActive() {
      return this.currentNamespace === this.namespace
    },

    /**
     * Compute if brand tiles should be rendered.
     * - Gallery namespace, desktop-only, odd number of slides.
     * @returns {Boolean}
     */
    renderBrandTiles() {
      return (
        this.brandTiles.length &&
        this.namespace === 'gallery' &&
        this.media.length % 2
      )
    },
  },

  watch: {

    /**
     * Watch current media index to update slide index whilst active.
     * - This is used to show variant media.
     */
    currentMediaIndex() {
      if (
        !this.namespaceActive ||
        this.currentMediaIndex === this.localSlideIndex
      ) {
        return
      }

      this.goToIndex(this.currentMediaIndex, true)
    },

    /**
     * Watch namespace active to update visible slide and pinch zooms.
     */
    async namespaceActive() {

      /**
       * Set slide position.
       * - Splide carousel doesn't update correctly when modal is hidden.
       */
      if (this.namespaceActive) {
        await this.goToIndex(this.currentMediaIndex)
      }

      /**
       * Enable/disable pinch zoom and zoom out on close.
       */
      this.updatePinchZoom()
    },

    /**
     * Watch pinch zoom active to disable carousel dragging.
     */
    pinchZoomActive() {
      this.localConfig.drag = !this.pinchZoomActive
    },
  },

  beforeMount() {
    this.localConfig = {
      direction: cnvs.store.language === 'ar' ? 'rtl' : 'ltr',
      drag: !this.isModal,
      flickPower: 200,
      flickMaxPages: 1,
      mediaQuery: 'min',
      speed: this.$timing('normal'),
      start: this.currentMediaIndex || 0,
      type: this.media.length === 1 ? 'slide' : 'loop',
      ...this.config,
    }

    /**
     * Destroy carousel on desktop.
     * - Doesn't work when being passed as config prop for some reason.
     */
    if (this.namespace === 'gallery') {
      this.localConfig.breakpoints = {
        [breakpoints.l]: {
          destroy: true,
        },
      }

      return
    }

    /**
     * Hide arrows and pagination in modal gallery when only one item.
     */
    if (this.media.length !== 1) {
      return
    }

    this.localConfig.arrows = false
    this.localConfig.pagination = false
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle Splide mounted event.
     * @param {Object} splide - Splide instance.
     */
    handleSplideMounted() {
      if (!this.zoom && !this.pinchZoom.length) {
        return
      }

      this.initPinchZoom()
    },

    /**
     * Handle Splide moved event.
     * - Updates current slide index to share with modal.
     * - Sets pinch zoom container if zoom prop is active.
     * @param {Object} splide - Splide instance.
     * @param {Number} newIndex - New slide index.
     * @param {Number} prevIndex - Previous slide index.
     * @param {Number} destIndex - Destination slide index.
     */
    handleSplideMoved(_, newIndex, prevIndex) {
      this.localSlideIndex = newIndex

      if (newIndex === prevIndex) {
        return
      }

      const newMediaPosition = this.media[newIndex].position
      this.$emit('update-current-media-position', newMediaPosition)

      if (!this.zoom) {
        return
      }

      if (this.pinchZoomActive) {
        this.pinchZoom[prevIndex].zoomOut()
      }

      this.pinchZoom[prevIndex].disable()
      this.pinchZoom[newIndex].enable()
    },

    /**
     * Go to slide.
     * - Set speed to 0 to instantly update slide without animation.
     * @param {Number} index - Slide index to go to.
     * @param {Boolean} animate - Animate slide transition.
     * @returns {Promise}
     */
    goToIndex(index, animate = false) {
      return new Promise((resolve) => {
        if (!animate) {
          this.localConfig.speed = 0
        }

        setTimeout(() => {
          this.$refs.splide.go(index)
        }, 0)

        if (!animate) {
          setTimeout(() => {
            this.localConfig.speed = this.config.speed || this.$timing('normal')
          }, 10)
        }

        resolve()
      })
    },

    /**
     * Get video MP4 source object.
     * @param {Array} sources - Video sources.
     * @returns {Object}
     */
    getVideoSource(sources) {
      return sources.find((source) => source.format === 'mp4')
    },

    /**
     * Get video active state.
     * - Play video on mobile when gallery is active and slide is visible.
     * - Play video on desktop when gallery is active.
     * @param {Number} position - Video media position.
     * @returns {Boolean}
     */
    getVideoActive(position) {
      return this.isMobile
        ? this.namespaceActive && position === this.currentMediaPosition
        : this.namespaceActive
    },

    /**
     * Initiate instances of pinch zoom.
     */
    async initPinchZoom() {
      this.startedPinchZoom = true

      await new Promise((resolve) => {
        nextTick().then(resolve)
      })

      this.pinchZoom = this.media.map((_, index) => {
        // eslint-disable-next-line new-cap
        return new this.pinchZoomApi(this.$refs.pinchZoom[index], {
          draggableUnzoomed: false,
          minZoom: 1,
          onDoubleTap: this.handleZoom,
          onZoomUpdate: this.handleZoom,
          tapZoomFactor: this.isMobile ? 2 : 3,
        })
      })

      /**
       * Disable pinch zooms not currently visible
       * - Library doesn't provide way of creating instances without
       *   automatically enabling them so have to disable after init.
       */
      this.pinchZoom.forEach((pinchZoom, index) => {
        if (index === this.currentMediaIndex) {
          return
        }

        pinchZoom.disable()
      })
    },

    /**
     * Update instances of pinch zoom when opening/closing modal.
     */
    updatePinchZoom() {
      if (!this.zoom) {
        return
      }

      /**
       * Only init pinch zoom if Splide has been mounted.
       */
      if (this.namespaceActive) {
        if (!this.startedPinchZoom && !this.pinchZoom.length) {
          this.initPinchZoom()
        }

        return
      }

      /**
       * Destroy pinch zoom on modal close.
       * - Updated destroy to not remove elements.
       */
      this.pinchZoom.forEach((pinchZoom) => {
        pinchZoom.destroy()
      })

      this.pinchZoomActive = false
      this.startedPinchZoom = false
      this.pinchZoom = []
    },

    /**
     * Handle zoom events.
     * - Use events to prevent carousel dragging when zoomed in.
     */
    handleZoom(pinch) {
      this.pinchZoomActive = pinch.zoomFactor > 1
    },

    /**
     * Handle zoom click.
     * - Inline check of buttons prop wasn't working.
     * @param {Number} position - Clicked media position.
     */
    handleZoomClick(position) {
      if (!this.buttons) {
        return
      }

      this.$emit('update-current-media-position', position)

      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery-carousel';
</style>
